import { useCallback, useEffect, useRef, useState } from "react";

/** Below this the list is not worth showing at all, so a drag cannot go under it. */
const MIN_HEIGHT_PX = 120;

/**
 * A height the reader sets by dragging an edge, for an element that is otherwise as tall as its
 * content.
 *
 * `heightPx` is `null` until the first drag: a height they did not ask for is exactly what the
 * fixed two fifths of the window was. It resets once `active` turns false, because a height set
 * by hand means nothing after the element is folded away and opened again, which is also the
 * reader's way back to the full height.
 *
 * Spread `handleProps` on the drag handle and attach `targetRef` to the element being resized.
 */
export function useResizableHeight(active: boolean) {
  const [heightPx, setHeightPx] = useState<number | null>(null);
  const [resizing, setResizing] = useState(false);
  const targetRef = useRef<HTMLDivElement>(null);
  const dragRef = useRef<{ startY: number; startHeight: number } | null>(null);

  useEffect(() => {
    if (!active) {
      setHeightPx(null);
    }
  }, [active]);

  const start = useCallback((event: React.PointerEvent<HTMLDivElement>) => {
    const current = targetRef.current?.getBoundingClientRect().height;
    if (current === undefined) {
      return;
    }
    dragRef.current = { startY: event.clientY, startHeight: current };
    setHeightPx(current);
    setResizing(true);
    // Captured, so the drag survives the pointer leaving a ten-pixel strip on the first move.
    event.currentTarget.setPointerCapture(event.pointerId);
  }, []);

  const move = useCallback((event: React.PointerEvent<HTMLDivElement>) => {
    const drag = dragRef.current;
    if (!drag) {
      return;
    }
    setHeightPx(Math.max(MIN_HEIGHT_PX, drag.startHeight + (event.clientY - drag.startY)));
  }, []);

  const end = useCallback((event: React.PointerEvent<HTMLDivElement>) => {
    dragRef.current = null;
    setResizing(false);
    event.currentTarget.releasePointerCapture(event.pointerId);
  }, []);

  // A blur, orientation change, or the OS taking the pointer for its own gesture fires
  // pointercancel/lostpointercapture instead of pointerup, so the drag has to end here too --
  // otherwise the handle stays "resizing" and the next unrelated pointer move keeps dragging it.
  const abort = useCallback(() => {
    dragRef.current = null;
    setResizing(false);
  }, []);

  return {
    heightPx,
    resizing,
    targetRef,
    handleProps: {
      onPointerDown: start,
      onPointerMove: move,
      onPointerUp: end,
      onPointerCancel: abort,
      onLostPointerCapture: abort,
    },
  };
}
